import React from 'react';
import SearchResultsLoader from '@components/BondDirectoryFeature/components/SearchResultsLoader';
import { Link, Text } from '@ui/atoms';
import { Pagination } from '@ui/atoms/design-system/pagination';
import { toPascalCaseWithSpaces } from '@utils/string-utils';
import { useBondDirectoryContext } from '../context/bond-directory-context';
import routes from '@helpers/routes';

const BondDirectoryResultsView = () => {
    const { loading, handlePageChange, searchResults } =
        useBondDirectoryContext(({ states, callbacks }) => ({
            loading: states.loading.results,
            searchResults: states.searchResults,
            handlePageChange: callbacks.handlePageChange,
        }));
    const tableRef = React.createRef();

    const { rows, count } = searchResults?.results || {};
    const searchQuery = searchResults?.search;
    const currentPage = searchResults?.page;

    return (
        <section className="p-4 pt-6 pb-12 md:p-6 xl:px-16 xl:py-8 bg-basicWhite">
            <div className="flex flex-col gap-6 md:gap-8 max-w-[720px] xl:max-w-[1152px] mx-auto">
                {loading || !rows ? (
                    <SearchResultsLoader className="flex-1 bg-basicWhite" />
                ) : rows?.length ? (
                    <>
                        <Text
                            content={
                                <>
                                    <span className="p3-semibold md:p1-semibold">
                                        {count}
                                    </span>{' '}
                                    search results for{' '}
                                    <span className="p3-semibold md:p1-semibold">
                                        "{searchQuery}"
                                    </span>
                                </>
                            }
                            className="text-gray-900 p3-regular xl:p1-regular"
                        />
                        <div className="grid grid-cols-1 gap-6 xl:gap-10 md:grid-cols-2 xl:grid-cols-3">
                            {rows?.map(
                                (
                                    {
                                        title,
                                        tags,
                                        dealDetails,
                                        footerDetails,
                                        isin,
                                        isDealLive,
                                    },
                                    index
                                ) => (
                                    <Link
                                        href={routes.bonds.byIsin(isin)}
                                        className="overflow-hidden shadow-md rounded-xl max-w-[360px] w-full mx-auto flex flex-col transition-all ease-in-out"
                                        key={`bond-${isin}-${index}`}
                                    >
                                        <div className="flex items-center px-4 md:px-6 py-3 md:h-[72px] bg-periwinkle-50">
                                            <Text
                                                content={title}
                                                className="transition-all ease-in-out h6-bold md:line-clamp-2 "
                                            />
                                        </div>
                                        <div className="flex flex-col flex-1 px-4 py-3 md:px-6 bg-basicWhite">
                                            <div className="flex items-center flex-1 gap-4 text-basicBlack">
                                                <div className="flex flex-wrap items-center gap-2 group">
                                                    {tags.map((tag, index) => (
                                                        <Text
                                                            key={index}
                                                            content={toPascalCaseWithSpaces(
                                                                tag
                                                            )}
                                                            title={toPascalCaseWithSpaces(
                                                                tag
                                                            )}
                                                            className="px-2 py-1 text-gray-900 capitalize border border-gray-300 rounded-full p6-regular md:p5-regular bg-basicWhite whitespace-nowrap"
                                                        />
                                                    ))}
                                                    {isDealLive ? (
                                                        <Text
                                                            key={index}
                                                            content={
                                                                'Live Deal'
                                                            }
                                                            className="px-2 py-1 bg-gradient-to-r from-secondary-200 to-secondary-400 rounded-[50px] flex-col justify-start items-start inline-flex p6-medium md:p6-medium bg-basicWhite whitespace-nowrap text-basicBlack"
                                                        />
                                                    ) : null}
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-4 mt-4">
                                                {dealDetails.map(
                                                    (
                                                        { label, value },
                                                        index
                                                    ) => (
                                                        <div
                                                            key={index}
                                                            className="flex flex-col gap-1"
                                                        >
                                                            <Text
                                                                content={label}
                                                                className="text-gray-500 p5-regular"
                                                            />
                                                            <Text
                                                                content={value}
                                                                className="text-primary-500 p3-bold"
                                                            />
                                                        </div>
                                                    )
                                                )}
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-4 px-4 py-4 md:px-6 bg-gray-50">
                                            {footerDetails.map(
                                                ({ label, value }, index) => (
                                                    <div
                                                        key={index}
                                                        className="flex flex-col gap-1"
                                                    >
                                                        <Text
                                                            content={label}
                                                            className="text-gray-600 p5-regular "
                                                        />
                                                        <Text
                                                            content={value}
                                                            title={value}
                                                            className="text-basicBlack p4-regular line-clamp-1"
                                                        />
                                                    </div>
                                                )
                                            )}
                                        </div>
                                    </Link>
                                )
                            )}
                        </div>
                    </>
                ) : (
                    <div
                        className={
                            'w-full min-h-[400px] justify-center flex flex-col items-center gap-2 px-6 p-16 md:px-16'
                        }
                    >
                        <Text
                            content={
                                searchQuery
                                    ? `0 search results for “${searchQuery}”`
                                    : 'No search results found'
                            }
                            className="text-center p3-semibold text-primary-500"
                        />
                        <Text
                            content={`Try searching for something else`}
                            className="text-center text-gray-600 p5-regular"
                        />
                    </div>
                )}
                {!loading && count && currentPage ? (
                    <Pagination
                        onChange={handlePageChange}
                        totalCount={count}
                        pageSize={12}
                        tableRef={tableRef}
                        skipSize={3}
                        hideOnSinglePage
                        defaultPage={currentPage}
                    />
                ) : null}
            </div>
        </section>
    );
};

export default BondDirectoryResultsView;
